// src/admin/pages/messages/ContactMessagesManagement.tsx
import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Alert,
  CircularProgress,
  Stack,
  Chip,
  Tooltip,
  Drawer,
  Divider,
  TextField,
  InputAdornment,
  Pagination,
  Collapse,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  useMediaQuery,
  Backdrop,
  LinearProgress,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Email as EmailIcon,
  MarkEmailRead as MarkEmailReadIcon,
  MarkEmailUnread as MarkEmailUnreadIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  WhatsApp as WhatsAppIcon,
} from '@mui/icons-material';
import { ContactMessage } from './contactMessageService';
import {
  useMessagesPaginated,
  useMessageStats,
  useMessageMutations,
} from '../../../hooks/useContactMessages';
import ToastNotification from '../common/Toastnotification';
import { useAuth } from '../../../contexts/AuthContext';

const MESSAGES_PER_PAGE = 10;

interface ToastState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
}

const getSoftChipSx = (color: string, isDark: boolean = false) => {
  type PaletteEntry = { bg: string; border: string; text: string; iconColor: string };

  const lightPalette: Record<string, PaletteEntry> = {
    warning: { bg: 'rgba(255,167,38,0.12)', border: 'rgba(255,167,38,0.4)', text: '#b76e00', iconColor: '#e68900' },
    info: { bg: 'rgba(41,182,246,0.12)', border: 'rgba(41,182,246,0.4)', text: '#0277bd', iconColor: '#039be5' },
    primary: { bg: 'rgba(33,150,243,0.12)', border: 'rgba(33,150,243,0.4)', text: '#1565c0', iconColor: '#1976d2' },
    success: { bg: 'rgba(102,187,106,0.15)', border: 'rgba(102,187,106,0.45)', text: '#2e7d32', iconColor: '#43a047' },
    error: { bg: 'rgba(239,83,80,0.12)', border: 'rgba(239,83,80,0.4)', text: '#c62828', iconColor: '#e53935' },
    secondary: { bg: 'rgba(171,71,188,0.10)', border: 'rgba(171,71,188,0.35)', text: '#6a1b9a', iconColor: '#8e24aa' },
    default: { bg: 'rgba(158,158,158,0.12)', border: 'rgba(158,158,158,0.4)', text: '#424242', iconColor: '#757575' },
  };

  const darkPalette: Record<string, PaletteEntry> = {
    warning: { bg: 'rgba(255,167,38,0.22)', border: 'rgba(255,167,38,0.55)', text: '#ffcc80', iconColor: '#ffa726' },
    info: { bg: 'rgba(41,182,246,0.20)', border: 'rgba(41,182,246,0.50)', text: '#81d4fa', iconColor: '#29b6f6' },
    primary: { bg: 'rgba(33,150,243,0.22)', border: 'rgba(33,150,243,0.50)', text: '#90caf9', iconColor: '#42a5f5' },
    success: { bg: 'rgba(102,187,106,0.22)', border: 'rgba(102,187,106,0.50)', text: '#a5d6a7', iconColor: '#66bb6a' },
    error: { bg: 'rgba(239,83,80,0.22)', border: 'rgba(239,83,80,0.50)', text: '#ef9a9a', iconColor: '#ef5350' },
    secondary: { bg: 'rgba(171,71,188,0.22)', border: 'rgba(171,71,188,0.50)', text: '#ce93d8', iconColor: '#ab47bc' },
    default: { bg: 'rgba(158,158,158,0.22)', border: 'rgba(158,158,158,0.50)', text: '#e0e0e0', iconColor: '#bdbdbd' },
  };

  const palette = isDark ? darkPalette : lightPalette;
  const p = palette[color] ?? palette.default;

  return {
    backgroundColor: p.bg,
    borderColor: p.border,
    color: p.text,
    fontWeight: 600,
    border: `1px solid ${p.border}`,
    '& .MuiChip-icon': { color: p.iconColor },
    '& .MuiChip-label': { color: p.text },
  };
};

interface LoadingOverlayProps {
  wrapperRef: React.RefObject<HTMLDivElement | null>;
}

const LoadingOverlay: React.FC<LoadingOverlayProps> = ({ wrapperRef }) => {
  const [rect, setRect] = useState<DOMRect | null>(null);
  const theme = useTheme();

  const overlayBg = theme.palette.mode === 'dark'
    ? 'rgba(26, 29, 36, 0.85)'
    : 'rgba(255, 255, 255, 0.85)';

  React.useEffect(() => {
    const measure = () => {
      if (wrapperRef.current) setRect(wrapperRef.current.getBoundingClientRect());
    };
    measure();
    const el = wrapperRef.current;
    window.addEventListener('resize', measure);
    el?.addEventListener('scroll', measure);
    return () => {
      window.removeEventListener('resize', measure);
      el?.removeEventListener('scroll', measure);
    };
  }, [wrapperRef]);

  if (!rect) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
        backgroundColor: overlayBg,
        backdropFilter: 'blur(2px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1100,
        pointerEvents: 'all',
        borderRadius: 'inherit',
      }}
    >
      <Box sx={{ textAlign: 'center' }}>
        <CircularProgress size={40} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Loading messages...
        </Typography>
      </Box>
    </Box>
  );
};

const ContactMessagesManagement: React.FC = () => {
  // ── UI state ──────────────────────────────────────────────────────────────
  const [deleteLoading, setDeleteLoading] = useState<string>('');
  const [statusLoading, setStatusLoading] = useState<string>('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState<ContactMessage | null>(null);
  const [toast, setToast] = useState<ToastState>({ open: false, message: '', severity: 'info' });
  const [deleteTarget, setDeleteTarget] = useState<ContactMessage | null>(null);
  const [deleteDialogLoading, setDeleteDialogLoading] = useState(false);

  // ── Pagination & filter state ─────────────────────────────────────────────
  // FIX: Removed all cursor state (lastVisible, firstVisible, cursors).
  // Seek-based pagination only needs the current page number.
  const [currentPage, setCurrentPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showFilters, setShowFilters] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const tableWrapperRef = useRef<HTMLDivElement>(null);

  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { hasPermission, isSuperAdmin } = useAuth();

  const canView = isSuperAdmin || hasPermission('messages.view');
  const canReply = isSuperAdmin || hasPermission('messages.reply');   // mark read/unread
  const canDelete = isSuperAdmin || hasPermission('messages.delete');

  // ── React Query hooks ─────────────────────────────────────────────────────
  // FIX: No longer passes a `cursors` object — just page number + filters.
  const {
    data: messagesData,
    isLoading: messagesLoading,
    isFetching: messagesIsFetching,
    isError: messagesIsError,
    error: messagesErrorObj,
  } = useMessagesPaginated({
    page: currentPage,
    search: searchQuery,
    status: statusFilter,
    pageSize: MESSAGES_PER_PAGE,
  });

  const { data: statsData } = useMessageStats();
  const { updateStatus, markAsRead, remove } = useMessageMutations();

  // ── Derived values ────────────────────────────────────────────────────────
  const messages = messagesData?.messages ?? [];
  const totalMessages = messagesData?.totalCount ?? 0;
  const totalPages = Math.ceil(totalMessages / MESSAGES_PER_PAGE);
  const stats = statsData ?? { total: 0, read: 0, unread: 0 };
  const loading = messagesLoading || messagesIsFetching;

  // ── Toast helper ──────────────────────────────────────────────────────────
  const showToast = (msg: string, severity: 'success' | 'error' | 'warning' | 'info') => {
    setToast({ open: true, message: msg, severity });
  };
  const handleCloseToast = () => setToast(prev => ({ ...prev, open: false }));

  // ── Reset to page 1 ───────────────────────────────────────────────────────
  // FIX: Simplified — no cursor state to reset, just set page to 1.
  const resetToFirstPage = () => {
    setCurrentPage(1);
  };

  // ── Pagination handler ────────────────────────────────────────────────────
  // FIX: Simplified — just set the page number directly. The seek-based
  // pagination in the service computes the correct Firestore offset from
  // the page number, so jumping to any page works correctly.
  const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
  };

  // ── Search ────────────────────────────────────────────────────────────────
  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setSearchQuery(searchInput);
      resetToFirstPage();
    }
  };

  // ── Filters ───────────────────────────────────────────────────────────────
  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
    resetToFirstPage();
  };

  const clearFilters = () => {
    setSearchInput('');
    setSearchQuery('');
    setStatusFilter('all');
    resetToFirstPage();
    showToast('Filters cleared', 'info');
  };

  const hasActiveFilters = searchQuery !== '' || statusFilter !== 'all';
  const activeFiltersCount = [searchQuery !== '', statusFilter !== 'all'].filter(Boolean).length;

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDeleteMessage = (message: ContactMessage) => {
    setDeleteTarget(message);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      setDeleteDialogLoading(true);
      setDeleteLoading(deleteTarget.id!);
      await remove.mutateAsync(deleteTarget.id!);
      if (selectedMessage?.id === deleteTarget.id) {
        setDrawerOpen(false);
        setSelectedMessage(null);
      }
      setDeleteTarget(null);
      resetToFirstPage();
      showToast('Message deleted successfully', 'success');
    } catch (err: any) {
      showToast(`Failed to delete message: ${err.message || 'Unknown error'}`, 'error');
    } finally {
      setDeleteDialogLoading(false);
      setDeleteLoading('');
    }
  };

  const handleDeleteCancel = () => setDeleteTarget(null);

  // ── Toggle read / unread ──────────────────────────────────────────────────
  const handleToggleStatus = async (message: ContactMessage) => {
    const newStatus = message.status === 'read' ? 'unread' : 'read';
    try {
      setStatusLoading(message.id!);
      await updateStatus.mutateAsync({ messageId: message.id!, status: newStatus });
      if (selectedMessage?.id === message.id) {
        setSelectedMessage({ ...message, status: newStatus });
      }
      resetToFirstPage();
      showToast(`Message marked as ${newStatus}`, 'success');
    } catch (err: any) {
      showToast(`Failed to update message status: ${err.message || 'Unknown error'}`, 'error');
    } finally {
      setStatusLoading('');
    }
  };

  // ── View message (auto-marks as read) ─────────────────────────────────────
  const handleViewMessage = async (message: ContactMessage) => {
    setSelectedMessage(message);
    setDrawerOpen(true);

    if (message.status === 'unread') {
      try {
        await markAsRead.mutateAsync(message.id!);
        setSelectedMessage({ ...message, status: 'read' });
        resetToFirstPage();
      } catch (err: any) {
        showToast('Failed to mark message as read', 'warning');
      }
    }
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedMessage(null);
  };

  if (!canView) {
    return (
      <Box sx={{ p: 3 }}>
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <EmailIcon sx={{ fontSize: 64, color: 'warning.main', mb: 2, opacity: 0.7 }} />
            <Typography variant="h5" gutterBottom>Access Denied</Typography>
            <Typography variant="body1" color="text.secondary">
              You don't have permission to view messages. Please contact your administrator.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // ── Initial full-page loading state ───────────────────────────────────────
  if (loading && messages.length === 0) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight="400px">
        <CircularProgress size={40} />
        <Typography variant="body1" sx={{ ml: 2 }}>Loading messages...</Typography>
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        height: { md: '100%' },
        display: 'flex',
        flexDirection: 'column',
        overflow: { xs: 'visible', md: 'hidden' },
        p: { xs: 2, sm: 3 },
      }}
    >
      {/* ── Fixed Header ── */}
      <Box sx={{ flexShrink: 0 }}>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
          <EmailIcon sx={{ fontSize: { xs: 28, md: 32 }, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.5rem', md: '2rem' } }}>
              Contact Messages
            </Typography>
            <Typography variant="body2" color="text.secondary">
              View and manage contact form submissions
            </Typography>
          </Box>
        </Stack>

        {/* {messagesIsError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {(messagesErrorObj as Error)?.message || 'Failed to fetch messages'}
          </Alert>
        )} */}

        {/* ── Stats Cards ── */}
        <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }} sx={{ mb: 3 }} alignItems="stretch">
          <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
            <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } }, flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography color="text.secondary" gutterBottom variant="body2" noWrap>Total Messages</Typography>
                <Typography variant="h5" component="div" color="text.primary" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' }, mb: 1 }}>
                  {stats.total}
                </Typography>
                <Box sx={{ mt: 'auto', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'warning.main', flexShrink: 0 }} />
                    <Typography variant="caption" color="warning.main" noWrap>{stats.unread} Unread</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'success.main', flexShrink: 0 }} />
                    <Typography variant="caption" color="success.main" noWrap>{stats.read} Read</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
            <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } }, flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography color="text.secondary" gutterBottom variant="body2" noWrap>Unread</Typography>
                <Typography variant="h5" component="div" color="text.primary" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' }, mb: 1 }}>
                  {stats.unread}
                </Typography>
                <Box sx={{ mt: 'auto' }}>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {stats.total > 0 ? `${((stats.unread / stats.total) * 100).toFixed(1)}% of total` : '—'}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
            <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } }, flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography color="text.secondary" gutterBottom variant="body2" noWrap>Read</Typography>
                <Typography variant="h5" component="div" color="text.primary" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' }, mb: 1 }}>
                  {stats.read}
                </Typography>
                <Box sx={{ mt: 'auto' }}>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {stats.total > 0 ? `${((stats.read / stats.total) * 100).toFixed(1)}% of total` : '—'}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
            <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } }, flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography color="text.secondary" gutterBottom variant="body2" noWrap>
                  {hasActiveFilters ? 'Matching Results' : 'This Page'}
                </Typography>
                <Typography variant="h5" component="div" color="text.primary" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' }, mb: 1 }}>
                  {messages.length}
                </Typography>
                <Box sx={{ mt: 'auto' }}>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {hasActiveFilters
                      ? `of ${totalMessages} total`
                      : `page ${currentPage} of ${Math.max(1, totalPages)}`}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* ── Search & Filters ── */}
        <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent="space-between"
          >
            <TextField
              placeholder="Search by name, email, phone, message... (Press Enter)"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyPress={handleSearchKeyPress}
              sx={{ flexGrow: 1, maxWidth: { sm: 400 } }}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
              <Button
                onClick={() => setShowFilters(!showFilters)}
                variant={hasActiveFilters ? 'contained' : 'outlined'}
                startIcon={<FilterListIcon />}
                endIcon={showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                size="small"
              >
                Filters {hasActiveFilters && `(${activeFiltersCount})`}
              </Button>
              {hasActiveFilters && (
                <Button onClick={clearFilters} variant="text" size="small" startIcon={<ClearIcon />}>
                  Clear
                </Button>
              )}
            </Stack>
          </Stack>

          <Collapse in={showFilters}>
            <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={statusFilter}
                      onChange={(e) => handleStatusFilterChange(e.target.value)}
                      label="Status"
                    >
                      <MenuItem value="all">All Status</MenuItem>
                      <MenuItem value="unread">Unread Only ({stats.unread})</MenuItem>
                      <MenuItem value="read">Read Only ({stats.read})</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={9}>
                  <Box sx={{ display: 'flex', alignItems: 'center', height: '40px' }}>
                    <Typography variant="body2" color="text.secondary">
                      {hasActiveFilters
                        ? `Found ${messages.length} messages matching your filters`
                        : `Showing ${messages.length} messages on this page`}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Collapse>
        </Paper>
      </Box>

      {/* ── Scrollable Body ── */}
      <Box
        sx={{
          flex: 1,
          overflow: { xs: 'visible', md: 'auto' },
          display: 'flex',
          flexDirection: 'column',
          minHeight: { xs: 'auto', md: 0 },
        }}
      >
        {messages.length === 0 && !loading && (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
            <Card sx={{ width: '100%', maxWidth: 500 }}>
              <CardContent sx={{ textAlign: 'center', py: 6 }}>
                <EmailIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
                <Typography variant="h5" color="text.secondary" gutterBottom>
                  {hasActiveFilters ? 'No Messages Match Your Filters' : 'No Messages Found'}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}>
                  {hasActiveFilters
                    ? 'Try adjusting your search or filter criteria.'
                    : "You haven't received any contact messages yet."}
                </Typography>
                {hasActiveFilters && (
                  <Button onClick={clearFilters} variant="outlined" startIcon={<ClearIcon />}>
                    Clear Filters
                  </Button>
                )}
              </CardContent>
            </Card>
          </Box>
        )}

        {messages.length > 0 && (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: { xs: 'auto', md: 0 } }}>
            <TableContainer
              component={Paper}
              ref={tableWrapperRef}
              sx={{
                flex: 1,
                overflow: { xs: 'auto', md: 'auto' },
                maxHeight: { md: '100%' },
                mb: 2,
                position: 'relative',
                '& .MuiTable-root': { minWidth: { xs: 500, md: 900 } },
              }}
            >
              <Table stickyHeader size={isMobile ? 'small' : 'medium'}>
                <TableHead>
                  <TableRow>
                    <TableCell>Sender</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Contact Details</TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Message Preview</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Received</TableCell>
                    {(canReply || canDelete) && (
                      <TableCell align="right">Actions</TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {messages.map((message) => (
                    <TableRow
                      key={message.id}
                      hover
                      sx={{
                        '&:hover': { backgroundColor: 'action.hover' },
                        '&:last-child td, &:last-child th': { borderBottom: 0 },
                        backgroundColor: message.status === 'unread' ? 'action.selected' : 'inherit',
                      }}
                    >
                      <TableCell>
                        <Stack direction="row" alignItems="flex-start" spacing={1}>
                          <EmailIcon
                            fontSize="small"
                            color={message.status === 'unread' ? 'warning' : 'action'}
                            sx={{ mt: 0.25, flexShrink: 0 }}
                          />
                          <Box minWidth={0}>
                            {message.name && (
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: message.status === 'unread' ? 700 : 500,
                                  fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {message.name}
                              </Typography>
                            )}
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                fontSize: { xs: '0.75rem', sm: '0.8rem' },
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {message.email}
                            </Typography>
                            <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                              {message.phone && (
                                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.25 }}>
                                  <PhoneIcon sx={{ fontSize: '0.7rem', color: 'text.secondary' }} />
                                  <Typography variant="caption" color="text.secondary" noWrap>
                                    {message.phone}
                                  </Typography>
                                </Stack>
                              )}
                              {message.whatsapp && (
                                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.25 }}>
                                  <WhatsAppIcon sx={{ fontSize: '0.7rem', color: '#25D366' }} />
                                  <Typography variant="caption" color="text.secondary" noWrap>
                                    {message.whatsapp}
                                  </Typography>
                                </Stack>
                              )}
                            </Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                display: { xs: 'block', sm: 'none' },
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                mt: 0.25,
                              }}
                            >
                              {message.createdAt.toLocaleDateString()} — {message.message.substring(0, 30)}{message.message.length > 30 ? '…' : ''}
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>

                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        <Stack spacing={0.5}>
                          {message.phone ? (
                            <Stack direction="row" alignItems="center" spacing={0.75}>
                              <PhoneIcon sx={{ fontSize: '0.9rem', color: 'text.secondary', flexShrink: 0 }} />
                              <Typography variant="body2" color="text.secondary" noWrap>
                                {message.phone}
                              </Typography>
                            </Stack>
                          ) : null}
                          {message.whatsapp ? (
                            <Stack direction="row" alignItems="center" spacing={0.75}>
                              <WhatsAppIcon sx={{ fontSize: '0.9rem', color: '#25D366', flexShrink: 0 }} />
                              <Typography variant="body2" color="text.secondary" noWrap>
                                {message.whatsapp}
                              </Typography>
                            </Stack>
                          ) : null}
                          {!message.phone && !message.whatsapp && (
                            <Typography variant="caption" color="text.disabled">—</Typography>
                          )}
                        </Stack>
                      </TableCell>

                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: 240,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            fontWeight: message.status === 'unread' ? 600 : 400,
                          }}
                        >
                          {message.message}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={message.status === 'read' ? 'Read' : 'Unread'}
                          size="small"
                          variant="outlined"
                          icon={message.status === 'read' ? <MarkEmailReadIcon /> : <MarkEmailUnreadIcon />}
                          sx={getSoftChipSx(message.status === 'read' ? 'success' : 'warning', isDark)}
                        />
                      </TableCell>

                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        <Typography variant="body2" color="text.secondary">
                          {message.createdAt.toLocaleDateString()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {message.createdAt.toLocaleTimeString()}
                        </Typography>
                      </TableCell>

                      {(canReply || canDelete) && (
                        <TableCell align="right">
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end" flexWrap="nowrap">
                            {canView && (
                              <Tooltip title="View Message">
                                <IconButton onClick={() => handleViewMessage(message)} size="small" color="primary">
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {canReply && (
                              <Tooltip title={message.status === 'read' ? 'Mark as Unread' : 'Mark as Read'}>
                                <IconButton
                                  onClick={() => handleToggleStatus(message)}
                                  disabled={statusLoading === message.id}
                                  size="small"
                                  color={message.status === 'read' ? 'warning' : 'success'}
                                >
                                  {statusLoading === message.id ? (
                                    <CircularProgress size={16} />
                                  ) : message.status === 'read' ? (
                                    <MarkEmailUnreadIcon fontSize="small" />
                                  ) : (
                                    <MarkEmailReadIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </Tooltip>
                            )}
                            {canDelete && (
                              <Tooltip title="Delete">
                                <IconButton
                                  onClick={() => handleDeleteMessage(message)}
                                  disabled={deleteLoading === message.id}
                                  size="small"
                                  color="error"
                                >
                                  {deleteLoading === message.id ? (
                                    <CircularProgress size={16} />
                                  ) : (
                                    <DeleteIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {loading && <LoadingOverlay wrapperRef={tableWrapperRef} />}

            <Paper elevation={2} sx={{ flexShrink: 0, borderRadius: 1 }}>
              <Box sx={{ py: 2, px: 2 }}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={{ xs: 1.5, sm: 3 }}
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ width: '100%' }}
                >
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    Showing {totalMessages === 0 ? 0 : (currentPage - 1) * MESSAGES_PER_PAGE + 1}–
                    {Math.min(currentPage * MESSAGES_PER_PAGE, totalMessages)} of {totalMessages} messages
                  </Typography>
                  <Pagination
                    count={Math.max(1, totalPages)}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    size={isMobile ? 'small' : 'medium'}
                    showFirstButton
                    showLastButton
                    disabled={loading || totalPages <= 1}
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    Page {currentPage} of {Math.max(1, totalPages)}
                  </Typography>
                </Stack>
              </Box>
            </Paper>
          </Box>
        )}
      </Box>

      {/* ── Message Detail Drawer ── */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleCloseDrawer}
        PaperProps={{ sx: { width: { xs: '100%', sm: 480 } } }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box
            sx={{
              px: 3, py: 2,
              borderBottom: 1, borderColor: 'divider',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              flexShrink: 0,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <EmailIcon color="primary" />
              <Typography variant="h6">Message Details</Typography>
            </Stack>
            <IconButton onClick={handleCloseDrawer} edge="end">
              <CloseIcon />
            </IconButton>
          </Box>

          <Box
            sx={{
              flex: 1, overflow: 'auto', px: 3, py: 3, minHeight: 0,
              '&::-webkit-scrollbar': { width: '6px' },
              '&::-webkit-scrollbar-track': { background: 'transparent' },
              '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.2)', borderRadius: '3px' },
              '&::-webkit-scrollbar-thumb:hover': { background: 'rgba(0,0,0,0.35)' },
            }}
          >
            {selectedMessage && (
              <Stack spacing={3}>
                {selectedMessage.name && (
                  <>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Name
                      </Typography>
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
                        <PersonIcon fontSize="small" color="action" />
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {selectedMessage.name}
                        </Typography>
                      </Stack>
                    </Box>
                    <Divider />
                  </>
                )}

                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Email
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
                    <EmailIcon fontSize="small" color={selectedMessage.status === 'unread' ? 'warning' : 'action'} />
                    <Typography variant="body1" sx={{ fontWeight: 600, wordBreak: 'break-all' }}>
                      {selectedMessage.email}
                    </Typography>
                  </Stack>
                </Box>

                <Divider />

                {selectedMessage.phone && (
                  <>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Contact Number
                      </Typography>
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
                        <PhoneIcon fontSize="small" color="action" />
                        <Typography variant="body1">{selectedMessage.phone}</Typography>
                      </Stack>
                    </Box>
                    <Divider />
                  </>
                )}

                {selectedMessage.whatsapp && (
                  <>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        WhatsApp Number
                      </Typography>
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
                        <WhatsAppIcon fontSize="small" sx={{ color: '#25D366' }} />
                        <Typography variant="body1">{selectedMessage.whatsapp}</Typography>
                      </Stack>
                    </Box>
                    <Divider />
                  </>
                )}

                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Received
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {selectedMessage.createdAt.toLocaleDateString(undefined, {
                      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
                    })}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {selectedMessage.createdAt.toLocaleTimeString()}
                  </Typography>
                </Box>

                <Divider />

                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Status
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <Chip
                      label={selectedMessage.status === 'read' ? 'Read' : 'Unread'}
                      size="small"
                      variant="outlined"
                      icon={selectedMessage.status === 'read' ? <MarkEmailReadIcon /> : <MarkEmailUnreadIcon />}
                      sx={getSoftChipSx(selectedMessage.status === 'read' ? 'success' : 'warning', isDark)}
                    />
                  </Box>
                </Box>

                <Divider />

                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Message
                  </Typography>
                  <Paper
                    sx={{
                      p: 2, mt: 1, bgcolor: 'action.hover',
                      border: '1px solid', borderColor: 'divider', borderRadius: 1,
                    }}
                  >
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
                      {selectedMessage.message}
                    </Typography>
                  </Paper>
                </Box>
              </Stack>
            )}
          </Box>

          <Divider />
          <Box
            sx={{
              px: 3, py: 2,
              display: 'flex', gap: 1.5, justifyContent: 'flex-end',
              flexWrap: 'wrap', flexShrink: 0,
            }}
          >
            {selectedMessage && (
              <>
                {canReply && (
                  <Button
                    onClick={() => handleToggleStatus(selectedMessage)}
                    variant="outlined"
                    size="small"
                    disabled={statusLoading === selectedMessage.id}
                    startIcon={
                      statusLoading === selectedMessage.id
                        ? <CircularProgress size={14} />
                        : selectedMessage.status === 'read'
                          ? <MarkEmailUnreadIcon />
                          : <MarkEmailReadIcon />
                    }
                  >
                    Mark as {selectedMessage.status === 'read' ? 'Unread' : 'Read'}
                  </Button>
                )}
                {canDelete && (
                  <Button
                    onClick={() => handleDeleteMessage(selectedMessage)}
                    variant="outlined"
                    color="error"
                    size="small"
                    disabled={deleteLoading === selectedMessage.id}
                    startIcon={
                      deleteLoading === selectedMessage.id
                        ? <CircularProgress size={14} />
                        : <DeleteIcon />
                    }
                  >
                    Delete
                  </Button>
                )}
              </>
            )}
            <Button onClick={handleCloseDrawer} variant="contained" size="small">
              Close
            </Button>
          </Box>
        </Box>
      </Drawer>

      {/* ── Delete Confirmation Dialog ── */}
      <Dialog open={!!deleteTarget} onClose={handleDeleteCancel} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Message</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the message from{' '}
            <strong>{deleteTarget?.name || deleteTarget?.email}</strong>?{' '}
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleDeleteCancel} disabled={deleteDialogLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            disabled={deleteDialogLoading}
            startIcon={deleteDialogLoading ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Backdrop
        open={deleteDialogLoading}
        sx={{
          zIndex: (t) => t.zIndex.drawer + 200,
          flexDirection: 'column',
          gap: 3,
          bgcolor: 'rgba(0,0,0,0.6)',
        }}
      >
        <Box
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 3,
            px: 5,
            py: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2.5,
            minWidth: 280,
            boxShadow: 24,
          }}
        >
          <CircularProgress size={48} thickness={4} />
          <Box sx={{ width: '100%' }}>
            <LinearProgress sx={{ borderRadius: 1, mb: 1.5 }} />
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600, textAlign: 'center', color: 'text.primary' }}
            >
              Deleting Message…
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', textAlign: 'center', mt: 0.5 }}
            >
              Please wait, do not close this window
            </Typography>
          </Box>
        </Box>
      </Backdrop>

      <ToastNotification
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={handleCloseToast}
      />
    </Box>
  );
};

export default ContactMessagesManagement;